import { styled, alpha } from '@mui/material/styles';
import { Box, Button, IconButton, Menu, MenuItem, Switch, Typography } from '@mui/material';

// Compact toolbar "pill" button shared by the dark-mode, sound, and language controls.
export const HeaderPillButton = styled(Button)(({ theme }) => ({
  minWidth: 0,
  height: 38,
  paddingLeft: '13px',
  paddingRight: '13px',
  gap: '7px',
  borderRadius: '19px',
  color: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.86 : 0.72),
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1,
  textTransform: 'none',
  backgroundColor: 'transparent',
  '& .MuiButton-startIcon': {
    marginRight: 0,
    marginLeft: 0,
    '& svg': { fontSize: 16 },
  },
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.08)
        : theme.palette.background.common,
    boxShadow:
      theme.palette.mode === 'dark'
        ? 'inset 0 0 0 1px rgba(255,255,255,.04)'
        : '0 1px 3px rgba(18, 34, 45, .08)',
  },
}));

// Shared layout for a control row inside the overflow menu.
const rowBase = (theme) => ({
  width: '100%',
  minHeight: 48,
  paddingLeft: theme.spacing(1.25),
  paddingRight: theme.spacing(1.25),
  paddingTop: theme.spacing(0.75),
  paddingBottom: theme.spacing(0.75),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1.5),
  color: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.88 : 0.76),
  backgroundColor: 'transparent',
});

export const HeaderControlRow = styled(Box)(({ theme }) => rowBase(theme));

export const HeaderToggleRow = styled(Box)(({ theme }) => ({
  ...rowBase(theme),
  border: 0,
  cursor: 'pointer',
  textAlign: 'left',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.055),
  },
  '&:focus-within': {
    outline: `2px solid ${alpha(theme.palette.primary.main, 0.55)}`,
    outlineOffset: 2,
  },
}));

export const HeaderRowLabelGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: 0,
}));

export const HeaderRowLabel = styled(Typography)({
  fontSize: 13,
  fontWeight: 650,
  lineHeight: 1,
});

export const HeaderRowSwitch = styled(Switch)(({ theme }) => ({
  width: 38,
  height: 22,
  padding: 0,
  pointerEvents: 'none',
  '& .MuiSwitch-switchBase': {
    padding: '2px',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: theme.palette.common.white,
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.primary.main,
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 18,
    height: 18,
    boxShadow: '0 1px 2px rgba(0,0,0,.18)',
  },
  '& .MuiSwitch-track': {
    borderRadius: 11,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.22)
        : alpha(theme.palette.common.black, 0.18),
    opacity: 1,
  },
}));

export const headerRowIconSx = (theme) => ({
  fontSize: 18,
  color: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.78 : 0.58),
});

// --- Language selector ---

// Compact language button shown inside the language control row.
export const LanguagePillButton = styled(Button)(({ theme }) => ({
  minWidth: 56,
  height: 30,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  gap: theme.spacing(0.25),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.86 : 0.7),
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.055)
      : alpha(theme.palette.common.black, 0.04),
  '& .MuiButton-endIcon': {
    marginLeft: 0,
    '& svg': { fontSize: 16 },
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
  },
}));

export const FlagSlot = styled('span')({
  width: 18,
  height: 18,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 'none',
});

// Language dropdown menu.
export const LanguageMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    marginTop: theme.spacing(0.5),
    minWidth: 112,
    padding: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius * 2,
    backgroundColor: theme.palette.background.common,
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 12px 28px rgba(0,0,0,.35)'
        : '0 12px 28px rgba(18,34,45,.12)',
    border: '1px solid',
    borderColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.08)
        : alpha(theme.palette.common.black, 0.06),
  },
  '& .MuiList-root': {
    padding: 0,
    display: 'grid',
    gap: theme.spacing(0.25),
  },
}));

export const LanguageMenuItem = styled(MenuItem)(({ theme }) => ({
  minHeight: 36,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  gap: theme.spacing(1),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.9 : 0.74),
  fontSize: 13,
  fontWeight: 700,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
  },
  '&.Mui-selected:hover, &:hover': {
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.1),
  },
}));

// --- Compact overflow menu (shown below the md breakpoint) ---

// The "⋮" trigger button; `open` highlights it while the menu is open.
export const HeaderMoreButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: 38,
  height: 38,
  padding: 0,
  borderRadius: '50%',
  color: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.86 : 0.7),
  backgroundColor: open
    ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08)
    : 'transparent',
  '&:hover': {
    backgroundColor: open
      ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.1)
      : alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.08 : 0.055),
  },
}));

export const OverflowMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    marginTop: theme.spacing(0.75),
    minWidth: 230,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius * 3,
    backgroundColor: theme.palette.background.common,
    border: '1px solid',
    borderColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.08)
        : alpha(theme.palette.common.black, 0.06),
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 16px 34px rgba(0,0,0,.42)'
        : '0 16px 34px rgba(18,34,45,.13)',
  },
  '& .MuiList-root': {
    padding: 0,
  },
}));

export const HeaderOverflowControls = styled(Box)(({ theme }) => ({
  display: 'grid',
  paddingLeft: theme.spacing(0.25),
  paddingRight: theme.spacing(0.25),
  '& > *:not(:last-child)': {
    borderBottom: '1px solid',
    borderColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.08)
        : alpha(theme.palette.common.black, 0.07),
  },
}));
